import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { getTodayIso, useDebounce } from '@odo/shared-ui';
import { useVirtualAppointmentsOverviewRows } from '../../../hooks/useVirtualAppointmentsOverview';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { AppointmentsModalFrame } from '../appointments/AppointmentsModalFrame';
import {
    EMPTY_OVERVIEW_FILTERS,
    buildOverviewQuery,
    hasActiveOverviewFilters,
    type OverviewFilters
} from './overviewFilters';
import { sortAfterHeaderClick, type OverviewSort, type OverviewSortKey } from './overviewSort';
import { DEFAULT_OVERVIEW_TAB, overviewTabConfig, type OverviewTab } from './overviewTabs';
import { useOverviewPaging } from './useOverviewPaging';
import { useOverviewSelection } from './useOverviewSelection';
import { usePracticeDoctorNames } from './usePracticeDoctorNames';
import { VirtualAppointmentOverviewPane } from './VirtualAppointmentOverviewPane';
import { VirtualAppointmentsFilterSidebar } from './VirtualAppointmentsFilterSidebar';
import { VirtualAppointmentsOverviewTable } from './VirtualAppointmentsOverviewTable';

const SEARCH_DEBOUNCE_MS = 300;

interface VirtualAppointmentsModalProps {
    onClose: () => void;
    /** The practice / virtual switch rendered in the header. */
    viewToggle?: ReactNode;
}

// VAs across every patient: status and filters left, the table, detail pane right.
export function VirtualAppointmentsModal({ onClose, viewToggle }: VirtualAppointmentsModalProps) {
    const todayIso = useMemo(() => getTodayIso(), []);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
    const [filters, setFilters] = useState<OverviewFilters>(EMPTY_OVERVIEW_FILTERS);
    const [tab, setTab] = useState<OverviewTab>(DEFAULT_OVERVIEW_TAB);
    // Each tab keeps its own sort, so Upcoming stays on due day while Open is newest first.
    const [sortOverrides, setSortOverrides] = useState<Partial<Record<OverviewTab, OverviewSort>>>(
        {}
    );

    const tabConfig = overviewTabConfig(tab);
    const sort = sortOverrides[tab] ?? tabConfig.defaultSort;
    const query = useMemo(
        () => buildOverviewQuery(tabConfig, filters, debouncedSearch, sort),
        [tabConfig, filters, debouncedSearch, sort]
    );
    const paging = useOverviewPaging(query);
    const list = useVirtualAppointmentsOverviewRows(query, paging.mode, paging.page);
    const selection = useOverviewSelection(list.items);
    const doctorByPracticeId = usePracticeDoctorNames();
    const [stackedModalOpen, setStackedModalOpen] = useState(false);

    const resetFilters = useCallback(() => {
        setFilters(EMPTY_OVERVIEW_FILTERS);
        setSearch('');
    }, []);
    const changeSort = useCallback(
        (column: OverviewSortKey) =>
            setSortOverrides((prev) => ({ ...prev, [tab]: sortAfterHeaderClick(sort, column) })),
        [tab, sort]
    );

    // The pane is the top layer: Escape closes it first, then the modal; a stacked confirm modal owns it.
    useEscapeKey(() => (selection.selectedId ? selection.close() : onClose()), !stackedModalOpen);

    const practiceId = selection.selectedItem?.patient.practiceId;
    return (
        <AppointmentsModalFrame
            title="Appointments"
            viewToggle={viewToggle}
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by patient name"
            onClose={onClose}
        >
            <div className="flex-1 flex min-h-0">
                <VirtualAppointmentsFilterSidebar
                    tab={tab}
                    onTabChange={setTab}
                    filters={filters}
                    onChange={setFilters}
                    onReset={() => setFilters(EMPTY_OVERVIEW_FILTERS)}
                />
                <div className="flex-1 min-w-0 flex flex-col">
                    <VirtualAppointmentsOverviewTable
                        tab={tabConfig}
                        list={list}
                        paging={paging}
                        sort={sort}
                        onSort={changeSort}
                        selectedId={selection.selectedId}
                        onSelect={selection.toggle}
                        todayIso={todayIso}
                        doctorByPracticeId={doctorByPracticeId}
                        filtersActive={
                            hasActiveOverviewFilters(filters) || debouncedSearch.trim() !== ''
                        }
                        onClearFilters={resetFilters}
                    />
                </div>
                {selection.selectedId && (
                    <VirtualAppointmentOverviewPane
                        item={selection.selectedItem}
                        failed={selection.failed}
                        descriptionDoc={selection.descriptionDoc}
                        createdBy={selection.createdBy}
                        resolvedBy={selection.resolvedBy}
                        cancellation={selection.cancellation}
                        descriptionState={selection.descriptionState}
                        onRetry={selection.retry}
                        doctorName={
                            practiceId ? (doctorByPracticeId.get(practiceId) ?? null) : null
                        }
                        todayIso={todayIso}
                        onClose={selection.close}
                        onStackedModalChange={setStackedModalOpen}
                    />
                )}
            </div>
        </AppointmentsModalFrame>
    );
}
